// src/components/pdf-viewer/PDFToolbar.tsx
//
// Two-row toolbar for the PDF viewer.
//   Row 1 (header strip): document title (left) + close button (right) +
//                         Comments badge toggle.
//   Row 2 (controls):     page nav | zoom controls | spacer |
//                         export / estimate / page-export action buttons.
//
// Both rows render on mobile and desktop; the rows just become more compact
// on narrow widths. The title is truncated with ellipsis when needed.
import React from 'react';
import {
  Button,
  InputNumber,
  Tooltip,
  Badge,
  Space,
  Divider,
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  MessageOutlined,
  ColumnWidthOutlined,
  ColumnHeightOutlined,
  DollarOutlined,
  CloseOutlined,
  FileTextOutlined,
  RobotOutlined,
} from '@ant-design/icons';

interface PDFToolbarProps {
  currentPage: number;
  totalPages: number;
  zoom: number;
  /** Actual rendered scale as a percentage (e.g. 78 for 78%). Used for the
   *  zoom label so fit-width / fit-height show a real number instead of
   *  truncated "Fit W" / "Fit H" labels. */
  effectiveZoomPercent?: number;
  annotationCount: number;
  showAnnotations: boolean;
  exportLoading: boolean;
  isMobile?: boolean;
  hideExport?: boolean;
  exportPdfUrl?: string;
  onExportPdf?: () => void;
  onGenerateEstimate?: () => void;
  estimateLoading?: boolean;
  /** When true, shows an "Ask AI" toggle button in the action row. */
  enableAssistant?: boolean;
  assistantActive?: boolean;
  onToggleAssistant?: () => void;
  /** Document title shown in the top row. */
  documentTitle?: string;
  /** Optional close handler — when provided, an × button appears in the
   *  top-right of the toolbar. Used by the full-screen viewer modal. */
  onClose?: () => void;
  onPageChange: (page: number) => void;
  onZoomChange: (zoom: number) => void;
  onToggleAnnotations: () => void;
  onExportPage: () => void;
}

export const PDFToolbar: React.FC<PDFToolbarProps> = ({
  currentPage,
  totalPages,
  zoom,
  effectiveZoomPercent,
  annotationCount,
  showAnnotations,
  exportLoading,
  isMobile = false,
  hideExport = false,
  exportPdfUrl,
  onExportPdf,
  onGenerateEstimate,
  estimateLoading = false,
  enableAssistant = false,
  assistantActive = false,
  onToggleAssistant,
  documentTitle,
  onClose,
  onPageChange,
  onZoomChange,
  onToggleAnnotations,
  onExportPage,
}) => {
  const handlePageInputChange = (value: number | null) => {
    if (value && value >= 1 && value <= totalPages) {
      onPageChange(value);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  // zoom: -1 = fit-to-width, -2 = fit-to-height, otherwise numeric scale
  const isFitToWidth = zoom === -1;
  const isFitToHeight = zoom === -2;
  const isFitMode = isFitToWidth || isFitToHeight;

  // Mobile uses finer 10% steps (25% was too coarse — e.g. 35% jumped to 60%).
  const zoomStep = isMobile ? 0.1 : 0.25;
  // In a fit mode, step from the ACTUAL rendered scale (~35% on a phone) rather
  // than 100%, so the first tap is a small change instead of a big jump.
  const effectiveScale = (effectiveZoomPercent ?? Math.round(zoom * 100)) / 100;
  const zoomBase = isFitMode ? (isMobile ? effectiveScale : 1.0) : zoom;

  const handleZoomIn = () => onZoomChange(Math.min(zoomBase + zoomStep, 3.0));
  const handleZoomOut = () => onZoomChange(Math.max(zoomBase - zoomStep, 0.25));
  const handleFitToWidth = () => onZoomChange(-1);
  const handleFitToHeight = () => onZoomChange(-2);

  // Always show the actual rendered percentage. Fit modes are still
  // distinguishable via the highlighted Fit-W / Fit-H buttons.
  const numericPercent = isFitMode
    ? (effectiveZoomPercent ?? 0)
    : Math.round(zoom * 100);
  const zoomLabel = `${numericPercent}%`;

  const containerStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d9d9d9',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  };

  // -------------------- Row 1: header strip --------------------
  const row1Style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: isMobile ? '6px 10px' : '8px 16px',
    borderBottom: '1px solid #f0f0f0',
    minHeight: isMobile ? 40 : 44,
  };

  const titleStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontSize: isMobile ? 14 : 15,
    fontWeight: 600,
    color: '#1f2937',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  // -------------------- Row 2: controls --------------------
  const row2Style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? 4 : 8,
    padding: isMobile ? '6px 10px' : '8px 16px',
    // Mobile: keep everything on ONE line, scroll horizontally if it overflows.
    // Desktop: allow wrapping as before.
    flexWrap: isMobile ? 'nowrap' : 'wrap',
    rowGap: isMobile ? 0 : 8,
    overflowX: isMobile ? 'auto' : 'visible',
  };

  const btnSize: 'small' | 'middle' = isMobile ? 'small' : 'middle';

  // Action buttons (Download → Ask AI): on mobile keep the normal (small)
  // height but widen ~25% for easier tapping — wider, not taller.
  const actionBtnSize: 'small' | 'middle' | 'large' = btnSize;
  const actionBtnStyle: React.CSSProperties | undefined = isMobile
    ? { width: 30 }
    : undefined;

  return (
    <div style={containerStyle}>
      {/* Row 1: Title + Close + Comments toggle */}
      <div style={row1Style}>
        <div style={titleStyle} title={documentTitle || ''}>
          <FileTextOutlined style={{ color: '#1890ff', flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {documentTitle || 'PDF Viewer'}
          </span>
        </div>

        <Badge count={annotationCount} showZero={false} size="small">
          <Tooltip title={showAnnotations ? 'Hide Comments' : 'Show Comments'}>
            <Button
              icon={<MessageOutlined />}
              onClick={onToggleAnnotations}
              type={showAnnotations ? 'primary' : 'default'}
              size={btnSize}
            >
              {isMobile ? null : 'Comments'}
            </Button>
          </Tooltip>
        </Badge>

        {onClose && (
          <Tooltip title="Close">
            <Button
              icon={<CloseOutlined />}
              onClick={onClose}
              size={btnSize}
              aria-label="Close"
            />
          </Tooltip>
        )}
      </div>

      {/* Row 2: Page nav | Zoom | Action buttons */}
      <div style={row2Style}>
        {/* Page navigation */}
        <Space size={isMobile ? 2 : 'small'} wrap={false}>
          <Tooltip title="Previous Page">
            <Button
              icon={<LeftOutlined />}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              size={btnSize}
            />
          </Tooltip>
          <InputNumber
            min={1}
            max={totalPages}
            value={currentPage}
            onChange={handlePageInputChange}
            style={{ width: isMobile ? 40 : 70 }}
            size={btnSize}
            controls={false}
          />
          <span style={{ color: '#666', fontSize: 13, whiteSpace: 'nowrap' }}>
            / {totalPages}
          </span>
          <Tooltip title="Next Page">
            <Button
              icon={<RightOutlined />}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              size={btnSize}
            />
          </Tooltip>
        </Space>

        {!isMobile && <Divider type="vertical" style={{ height: 24 }} />}

        {/* Zoom controls */}
        <Space size={isMobile ? 2 : 'small'} wrap={false}>
          <Tooltip title="Zoom Out">
            <Button
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={!isFitMode && zoom <= 0.25}
              size={btnSize}
            />
          </Tooltip>
          <span
            style={{
              color: '#666',
              fontSize: 13,
              minWidth: isMobile ? 44 : 54,
              textAlign: 'center',
              display: 'inline-block',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {zoomLabel}
          </span>
          <Tooltip title="Zoom In">
            <Button
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={zoom >= 3.0}
              size={btnSize}
            />
          </Tooltip>
          {/* Fit-to-width / fit-to-height are no-ops on mobile (page already
              fills the screen width), so hide them there. */}
          {!isMobile && (
            <>
              <Tooltip title="Fit to Width">
                <Button
                  icon={<ColumnWidthOutlined />}
                  onClick={handleFitToWidth}
                  type={isFitToWidth ? 'primary' : 'default'}
                  size={btnSize}
                />
              </Tooltip>
              <Tooltip title="Fit to Height">
                <Button
                  icon={<ColumnHeightOutlined />}
                  onClick={handleFitToHeight}
                  type={isFitToHeight ? 'primary' : 'default'}
                  size={btnSize}
                />
              </Tooltip>
            </>
          )}
        </Space>

        {/* Spacer pushes the action-button group to the right on both mobile and
            desktop. With row2 set to nowrap on mobile it still stays one line
            (and scrolls only if genuinely too narrow). */}
        <div style={{ flex: 1, minWidth: 0 }} />

        {/* Action buttons */}
        <Space size={isMobile ? 8 : 'small'} wrap={false}>
          {!hideExport && (
            <Tooltip title="Export current page as PNG">
              <Button
                icon={<DownloadOutlined />}
                onClick={onExportPage}
                loading={exportLoading}
                size={actionBtnSize}
                style={actionBtnStyle}
              />
            </Tooltip>
          )}

          {(exportPdfUrl || onExportPdf) && (
            <Tooltip title="Download PDF with annotations">
              <Button
                icon={<FilePdfOutlined />}
                onClick={onExportPdf || (() => window.open(exportPdfUrl, '_blank'))}
                size={actionBtnSize}
                style={actionBtnStyle}
              />
            </Tooltip>
          )}

          {onGenerateEstimate && (
            <Tooltip title="Generate cost estimate">
              <Button
                icon={<DollarOutlined />}
                onClick={onGenerateEstimate}
                loading={estimateLoading}
                size={actionBtnSize}
                style={actionBtnStyle}
              />
            </Tooltip>
          )}

          {enableAssistant && onToggleAssistant && (
            <Tooltip title="Ask AI about this report">
              <Button
                icon={<RobotOutlined />}
                onClick={onToggleAssistant}
                size={actionBtnSize}
                style={actionBtnStyle}
                type={assistantActive ? 'primary' : 'default'}
              >
                {isMobile ? null : 'Ask AI'}
              </Button>
            </Tooltip>
          )}
        </Space>
      </div>
    </div>
  );
};
